import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { getAccessToken, getRefreshToken } from './auth/tokenStorage';
import { ThemeProvider } from './ThemeContext';
import { configureInterceptors } from './api/client';
import { msalInstance } from './auth/msalInstance';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { TooltipProvider } from './components/ui/tooltip';
import { Toaster } from './components/ui/sonner';
import { LoginPage } from './pages/LoginPage';
import { SetupPage } from './pages/SetupPage';
import { FlowsPage } from './pages/FlowsPage';
import { FlowBuilderPage } from './pages/FlowBuilderPage';
import { ExecutionsPage } from './pages/ExecutionsPage';
import { ExecutionDetailPage } from './pages/ExecutionDetailPage';
import { DlqPage } from './pages/DlqPage';
import { AppsPage } from './pages/AppsPage';
import { TokensPage } from './pages/TokensPage';
import { EnvPage } from './pages/EnvPage';
import { TagsPage } from './pages/TagsPage';
import { InspectorPage } from './pages/InspectorPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminApiKeysPage } from './pages/AdminApiKeysPage';
import { AdminBackupPage } from './pages/AdminBackupPage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

function InterceptorSetup() {
  const { setTokens, logout } = useAuth();
  useEffect(() => {
    configureInterceptors(getAccessToken, getRefreshToken, setTokens, logout);
  }, [setTokens, logout]);
  return null;
}

function LayoutWrapper() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function AdminRoute() {
  const { role } = useAuth();
  if (role !== 'Admin') return <Navigate to="/flows" replace />;
  return <Outlet />;
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/setup', element: <SetupPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <LayoutWrapper />,
        children: [
          { path: '/flows', element: <FlowsPage /> },
          { path: '/flows/:id', element: <FlowBuilderPage /> },
          { path: '/tags', element: <TagsPage /> },
          { path: '/executions', element: <ExecutionsPage /> },
          { path: '/executions/:id', element: <ExecutionDetailPage /> },
          { path: '/dlq', element: <DlqPage /> },
          { path: '/apps', element: <AppsPage /> },
          { path: '/tokens', element: <TokensPage /> },
          { path: '/env', element: <EnvPage /> },
          { path: '/inspector', element: <InspectorPage /> },
          {
            element: <AdminRoute />,
            children: [
              { path: '/admin/users', element: <AdminUsersPage /> },
              { path: '/admin/api-keys', element: <AdminApiKeysPage /> },
              { path: '/admin/backup', element: <AdminBackupPage /> },
            ],
          },
          { path: '/', element: <Navigate to="/flows" replace /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider>
        <TooltipProvider delayDuration={200}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <InterceptorSetup />
              <RouterProvider router={router} />
              <Toaster />
            </AuthProvider>
          </QueryClientProvider>
        </TooltipProvider>
      </ThemeProvider>
    </MsalProvider>
  );
}
